import { UnitSwitcher } from '@app/components/products/UnitSwitcher';
import { Calendar } from '@app/components/various/Icons';
import { Skeleton } from '@app/components/various/Skeleton';
import { getAllDeliveryWindowsByBuyerId, getFilteredDelwinIds, getStockTypeFilter, getStockTypeLabels } from '@app/ducks';
import { isSwitchAvailable } from '@app/logic/unitsSwitch';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ProductMatrixSectionGeneral } from '../ProductMatrixSectionGeneral';

interface Props {
  scannedBarcode: string | null;
  productDetails: Product.Standard;
  variants: (Product.Full | Product.Standard)[];
  isReadOnly?: boolean;
  deliveryWindowIds: string[];
  deliveryWindowIdsToShow: string[];
}

export const FilteredProductMatrixSection = ({
  productDetails,
  variants,
  isReadOnly,
  deliveryWindowIds,
  deliveryWindowIdsToShow,
  scannedBarcode,
}: Props) => {
  const stockType = useSelector(getStockTypeFilter);
  const stockTypeLabels = useSelector(getStockTypeLabels);
  const { t } = useTranslation(['products']);
  const filteredDelwinIds = useSelector(getFilteredDelwinIds);
  const allDeliveryWindowsByBuyerId = useSelector(getAllDeliveryWindowsByBuyerId);

  const matrixProps = {
    isReadOnly,
    variants,
  };

  const otherDelwinIds = deliveryWindowIds.filter(id => !deliveryWindowIdsToShow.includes(id));
  const isProductPage = location.pathname.indexOf('/products/') !== -1;

  const sizeSwitcher = isSwitchAvailable(productDetails.tableMappings) && (
    <div className="flex justify-end">
      <UnitSwitcher tableMappings={productDetails.tableMappings} defaultUnit={productDetails.defaultLocalizedChart} />
    </div>
  );

  if (isEmpty(allDeliveryWindowsByBuyerId)) {
    return (
      <div className="mx-auto h-full w-full max-w-[1920px] lg:flex">
        <Skeleton type="rectangle" className="h-48 w-full" />
      </div>
    );
  }

  if (!isProductPage) {
    return (
      <div className="flex w-full flex-col gap-4">
        {sizeSwitcher}
        <ProductMatrixSectionGeneral {...matrixProps} showOnlyDeliveryWindows={deliveryWindowIds} scannedBarcode={scannedBarcode} />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {sizeSwitcher}
      <ProductMatrixSectionGeneral {...matrixProps} showOnlyDeliveryWindows={deliveryWindowIdsToShow} scannedBarcode={scannedBarcode} />
      {!isEmpty(otherDelwinIds) && (
        <div className="mt-4 flex gap-2 rounded-regular bg-neutral-96 px-2 py-1.5 text-neutral-20 body-1">
          <Calendar className="mt-0.5 size-4 shrink-0 text-neutral-40" />
          <span>
            {!isDefined(filteredDelwinIds) || isEmpty(filteredDelwinIds) ?
              t('products:available_in_stock_type', {
                stockType: stockType === 'preorder' ? stockTypeLabels.stock : stockTypeLabels.preorder,
              })
            : t('products:available_in_other_delwins')}
          </span>
        </div>
      )}
    </div>
  );
};
